import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Copy, CheckCircle2, Clock, Trash2, Eye, KeyRound, Disc3, LayoutGrid, Wallet } from "lucide-react";
import { managersMe, managersMyReleases, managersAddArtist, managersRemoveArtist, managersGrantArtist, managersRevokeArtist, managersAddMember, managersRemoveMember, managersUpdateMember } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";
import ManagerProject from "@/components/ManagerProject";
import ManagerFinance from "@/components/ManagerFinance";
import { BucketTabs, StatusCell, bucketOf } from "@/components/ReleaseStatus";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const errText = (e) => e?.response?.data?.detail || "No se pudo completar";
const signLink = (token) => `${window.location.origin}/acuerdo/${token}`;
const assetUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

// Mismos permisos que The Orchard: por persona, con interruptores.
const PERMS = [
  { key: "admin", label: "Administrador de la cuenta", desc: "Invita personas y gestiona sus permisos." },
  { key: "catalog", label: "Catálogo", desc: "Sube lanzamientos y gestiona artistas." },
  { key: "accounting", label: "Contabilidad", desc: "Ve porcentajes, regalías y estados de cuenta." },
  { key: "insights", label: "Insights", desc: "Ve lanzamientos, estados y métricas." },
];
// El artista hace lo que el administrador le asigne, siempre SOLO sobre su propio perfil (nunca administra).
const ARTIST_PERMS = [
  { key: "catalog", label: "Catálogo", desc: "Sube y prepara lanzamientos solo para su perfil." },
  { key: "accounting", label: "Contabilidad", desc: "Ve su porcentaje y sus regalías." },
  { key: "insights", label: "Insights", desc: "Ve sus lanzamientos, estados y métricas." },
];
const DEFAULT_PERMS = { admin: false, catalog: true, accounting: false, insights: true };

function Toggle({ on, onChange, disabled }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!on)} className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-40 ${on ? "bg-emerald-500" : "bg-slate-300"}`} aria-pressed={on}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

function PermGrid({ value, onChange, list = PERMS }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {list.map((p) => (
        <label key={p.key} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <span><span className="block text-sm font-semibold text-black">{p.label}</span><span className="block text-[11px] text-slate-500">{p.desc}</span></span>
          <Toggle on={!!value[p.key]} onChange={(v) => onChange({ ...value, [p.key]: v })} />
        </label>
      ))}
    </div>
  );
}

const permChips = (p) => PERMS.filter((x) => p?.[x.key]).map((x) => x.label.replace("Administrador de la cuenta", "Admin"));

function NewReleasePicker({ artists, onPick, onClose }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="new-release-picker">
      <div className="mb-2 text-sm font-semibold text-black">¿Para qué artista es el lanzamiento?</div>
      <p className="mb-3 text-xs text-slate-500">Queda guardado en el perfil de ese artista. Si colabora otro artista de tu lista, agrégalo como artista en el formulario y saldrá también en su proyecto.</p>
      <div className="flex flex-wrap gap-2">
        {artists.map((a) => <button key={a.id} onClick={() => onPick(a.id)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:border-black">{a.name}</button>)}
        <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-slate-500 hover:text-black">Cancelar</button>
      </div>
    </div>
  );
}

export default function ManagerPanel({ initialView = "global" }) {
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);
  const [rels, setRels] = useState([]);
  const [view, setView] = useState(initialView);        // "global" | "finanzas" | id de un artista
  useEffect(() => { setView(initialView); }, [initialView]);
  const [bucket, setBucket] = useState("all");
  const [artistFilter, setArtistFilter] = useState("");
  const [picker, setPicker] = useState(false);
  const [newArtist, setNewArtist] = useState("");
  const [invite, setInvite] = useState(null);           // { email, perms }
  const [editMember, setEditMember] = useState(null);   // { id, perms }
  const [accessFor, setAccessFor] = useState(null);     // { artistId, email, perms }

  const load = useCallback(() => {
    managersMe().then(setD).catch(() => setD(null));
    managersMyReleases().then((r) => setRels(r?.releases || [])).catch(() => setRels([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (d === undefined) return <Loading />;
  if (!d?.manager) return <p className="text-sm text-slate-500">Tu cuenta no pertenece a ninguna subcuenta de manager o sello.</p>;

  const m = d.manager;
  const p = d.perms || {};
  const isArtist = d.role === "artist";
  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); return true; } catch (e) { toast.error(errText(e)); return false; } };
  const accessOf = (aid) => (d.artist_access || []).find((x) => x.artist_id === aid);
  const ownId = isArtist ? d.artists?.[0]?.id : null;
  const finance = !!(m.finance_enabled && p.accounting);
  const current = isArtist ? ownId : view;
  const shown = rels.filter((r) => (!artistFilter || (r.artist_ids || []).includes(artistFilter)) && (bucket === "all" || bucketOf(r) === bucket));
  const startNew = (aid) => navigate(`/distribute?para=${encodeURIComponent(aid)}`);

  return (
    <div className="space-y-8" data-testid="manager-panel">
      <div className="flex items-center gap-4 rounded-2xl p-5 text-white" style={{ background: m.color_accent || "#270A0A" }}>
        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-white/10">
          {m.logo_url ? <img src={assetUrl(m.logo_url)} alt="" className="h-full w-full object-contain" /> : <span className="text-xl font-bold">{m.name?.charAt(0)}</span>}
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-2xl font-bold">{m.name}</h1>
          <div className="text-xs text-white/70">{d.role === "owner" ? "Dueño de la cuenta" : isArtist ? `Artista · ${permChips(p).join(" · ") || "sin permisos"}` : `Permisos: ${permChips(p).join(" · ") || "ninguno"}`} · Powered by Always Fresh</div>
        </div>
        {!isArtist && <div className="text-right text-sm"><div className="text-2xl font-bold" style={{ color: m.color_primary }}>{d.artists.length}/{m.max_artists}</div><div className="text-[11px] text-white/70">artistas</div></div>}
      </div>

      {/* ---------------- global de la subcuenta y proyectos por artista */}
      {!isArtist && (p.insights || p.catalog || p.accounting) && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="manager-views">
          <button onClick={() => setView("global")} className={`flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold ${view === "global" ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-700 hover:border-black"}`}><LayoutGrid className="h-3.5 w-3.5" /> Global</button>
          {finance && <button onClick={() => setView("finanzas")} className={`flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold ${view === "finanzas" ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-700 hover:border-black"}`}><Wallet className="h-3.5 w-3.5" /> Finanzas</button>}
          {d.artists.map((a) => (
            <button key={a.id} onClick={() => setView(a.id)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold ${view === a.id ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-700 hover:border-black"}`}>{a.name}</button>
          ))}
        </div>
      )}

      {!isArtist && view === "finanzas" && (finance ? <ManagerFinance artists={d.artists} /> : <p className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">Finanzas del sello no está activada para tu cuenta. Pídesela a Always Fresh.</p>)}
      {current && current !== "global" && current !== "finanzas" && <ManagerProject artistId={current} />}
      {isArtist && ownId && finance && <ManagerFinance artists={d.artists} />}
      {isArtist && !ownId && <p className="text-sm text-slate-500">Aún no tienes un perfil asignado en esta subcuenta.</p>}

      {!isArtist && view === "global" && (p.insights || p.catalog) && (
        <section className="space-y-3">
          <SectionTitle right={p.catalog && d.artists.length > 0 && !picker && (
            <button onClick={() => setPicker(true)} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white" data-testid="new-release"><Plus className="h-4 w-4" /> Nuevo lanzamiento</button>
          )}>Lanzamientos de la subcuenta</SectionTitle>
          {picker && <NewReleasePicker artists={d.artists} onPick={startNew} onClose={() => setPicker(false)} />}
          {rels.length > 0 && <BucketTabs rows={rels} value={bucket} onChange={setBucket} />}
          {d.artists.length > 1 && (
            <select value={artistFilter} onChange={(e) => setArtistFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="global-artist-filter">
              <option value="">Todos los artistas</option>
              {d.artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {shown.map((r, i) => (
              <div key={r.release_id || i} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <Disc3 className="h-4 w-4 text-slate-300" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-black">{r.title}</div>
                  <div className="text-xs text-slate-500">
                    {(r.artist_ids || []).map((aid, j) => (
                      <span key={aid}>{j > 0 && <span className="text-slate-300"> · </span>}<button onClick={() => setView(aid)} className="hover:text-black hover:underline">{r.artists?.[j]}</button></span>
                    ))}
                    {r.release_date ? ` · ${r.release_date}` : ""}{r.assigned ? " · asignado por Always Fresh" : ""}
                  </div>
                </div>
                <StatusCell r={r} />
              </div>
            ))}
            {!shown.length && <p className="px-5 py-8 text-center text-sm text-slate-400">{rels.length ? "Nada con ese filtro." : "Todavía no hay lanzamientos. Las métricas de streams aparecerán cuando estén en tiendas."}</p>}
          </div>
        </section>
      )}

      {/* ---------------- artistas de la lista (gestión) */}
      {!isArtist && view === "global" && (
        <section>
          <SectionTitle>Artistas</SectionTitle>
          {p.catalog && (
            <div className="mb-3 flex gap-2">
              <input className={inputCls} value={newArtist} onChange={(e) => setNewArtist(e.target.value)} placeholder={d.slots_left ? "Nombre artístico del nuevo artista" : "Llegaste a tu límite de artistas"} disabled={!d.slots_left} />
              <button disabled={!d.slots_left || !newArtist.trim()} onClick={async () => { if (await run(() => managersAddArtist({ name: newArtist.trim() }), "Artista agregado. Envíale el link para firmar su acuerdo.")) setNewArtist(""); }} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"><Plus className="h-4 w-4" /> Agregar</button>
            </div>
          )}
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {d.artists.map((a) => {
              const acc = accessOf(a.id);
              return (
                <div key={a.id} className="px-5 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={() => setView(a.id)} className="min-w-0 flex-1 text-left font-medium text-black hover:underline">{a.name}</button>
                    {a.split?.artist_pct != null && <span className="text-xs text-slate-500">{a.split.artist_pct}% artista</span>}
                    {a.split?.status === "signed"
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Firmado</span>
                      : a.split?.status !== "none" && <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Clock className="h-3.5 w-3.5" /> Falta firma</span>}
                    {p.catalog && a.split?.status !== "signed" && a.split?.token && (
                      <button onClick={() => { navigator.clipboard.writeText(signLink(a.split.token)); toast.success("Link copiado: mándaselo al artista por WhatsApp"); }} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-black"><Copy className="h-3 w-3" /> Link de firma</button>
                    )}
                    {p.admin && (acc
                      ? <>
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600" title={acc.email}><Eye className="h-3 w-3" /> {acc.email}</span>
                          <span className="flex flex-wrap gap-1">{permChips(acc.perms).map((c) => <span key={c} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{c}</span>)}</span>
                          <button onClick={() => setEditMember(editMember?.id === acc.id ? null : { id: acc.id, perms: { ...acc.perms } })} className="text-xs font-semibold text-slate-600 hover:text-black">Permisos</button>
                          <button onClick={() => run(() => managersRevokeArtist(a.id), "Acceso quitado")} className="text-xs font-semibold text-slate-500 hover:text-red-600">Quitar acceso</button>
                        </>
                      : <button onClick={() => setAccessFor({ artistId: a.id, email: "", perms: { catalog: false, accounting: true, insights: true } })} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-black"><KeyRound className="h-3 w-3" /> Dar acceso a su proyecto</button>)}
                    {p.catalog && <button onClick={() => { if (window.confirm(`¿Quitar a ${a.name} de tu lista?`)) run(() => managersRemoveArtist(a.id), "Artista quitado"); }} className="text-slate-400 hover:text-red-500" title="Quitar"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                  {acc && editMember?.id === acc.id && (
                    <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3">
                      <PermGrid list={ARTIST_PERMS} value={editMember.perms} onChange={(v) => setEditMember({ ...editMember, perms: v })} />
                      <button onClick={async () => { if (await run(() => managersUpdateMember(acc.id, { ...editMember.perms, admin: false }), "Permisos guardados")) setEditMember(null); }} className="rounded-lg bg-black px-3 py-2 text-xs font-semibold text-white">Guardar permisos</button>
                    </div>
                  )}
                  {accessFor?.artistId === a.id && (
                    <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3">
                      <input className={inputCls} value={accessFor.email} onChange={(e) => setAccessFor({ ...accessFor, email: e.target.value })} placeholder="Correo de la cuenta del artista" />
                      <PermGrid list={ARTIST_PERMS} value={accessFor.perms} onChange={(v) => setAccessFor({ ...accessFor, perms: v })} />
                      <p className="text-[11px] text-slate-500">El artista entra a SU proyecto y hace solo lo que le actives aquí. Nunca ve a los otros artistas ni la parte del manager.</p>
                      <div className="flex gap-2">
                        <button onClick={async () => { if (await run(() => managersGrantArtist(a.id, accessFor.email.trim(), accessFor.perms), "Acceso dado")) setAccessFor(null); }} className="rounded-lg bg-black px-3 py-2 text-xs font-semibold text-white">Dar acceso</button>
                        <button onClick={() => setAccessFor(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-xs">Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {!d.artists.length && <p className="px-5 py-8 text-center text-sm text-slate-400">Aún no hay artistas.</p>}
          </div>
        </section>
      )}

      {/* ---------------- equipo (administradores de la cuenta) */}
      {p.admin && view === "global" && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <SectionTitle><span className="flex items-center gap-2"><Users className="h-5 w-5" /> Usuarios de la cuenta</span></SectionTitle>
            {!invite && <button disabled={!d.team_slots_left} onClick={() => setInvite({ email: "", perms: { ...DEFAULT_PERMS } })} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"><Plus className="h-4 w-4" /> Invitar usuario</button>}
          </div>
          <p className="mb-3 text-sm text-slate-500">Cada persona necesita una cuenta en Always Fresh con ese correo. {d.team_slots_left ? `Te quedan ${d.team_slots_left} cupo(s).` : "Llegaste al límite de tu equipo."}</p>
          {invite && (
            <div className="mb-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <input className={inputCls} value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="correo@equipo.com" />
              <PermGrid value={invite.perms} onChange={(v) => setInvite({ ...invite, perms: v })} />
              <div className="flex gap-2">
                <button onClick={async () => { if (await run(() => managersAddMember({ email: invite.email.trim(), perms: invite.perms }), "Usuario agregado")) setInvite(null); }} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white">Enviar invitación</button>
                <button onClick={() => setInvite(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancelar</button>
              </div>
            </div>
          )}
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {(d.team || []).map((t) => (
              <div key={t.id} className="px-5 py-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">{t.email}</div>
                  <div className="flex flex-wrap gap-1">{permChips(t.perms).map((c) => <span key={c} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{c}</span>)}</div>
                  <button onClick={() => setEditMember(editMember?.id === t.id ? null : { id: t.id, perms: { ...t.perms } })} className="text-xs font-semibold text-slate-600 hover:text-black">Permisos</button>
                  <button onClick={() => { if (window.confirm(`¿Quitar a ${t.email}?`)) run(() => managersRemoveMember(t.id), "Quitado"); }} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {editMember?.id === t.id && (
                  <div className="mt-3 space-y-2">
                    <PermGrid value={editMember.perms} onChange={(v) => setEditMember({ ...editMember, perms: v })} />
                    <button onClick={async () => { if (await run(() => managersUpdateMember(t.id, editMember.perms), "Permisos guardados")) setEditMember(null); }} className="rounded-lg bg-black px-3 py-2 text-xs font-semibold text-white">Guardar permisos</button>
                  </div>
                )}
              </div>
            ))}
            {!(d.team || []).length && <p className="px-5 py-6 text-center text-sm text-slate-400">Solo tú por ahora.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
